/**
 * CompactDdlFooterActions — the shared [Design table] [Close] pair every
 * compact DDL surface carries: the read-only `CompactDdlCard` AND each
 * editable `CompactDdlEditor` arm. Extracted (from `CompactDdlCard.tsx`) so the
 * `vqe-ddl-design-table` / `vqe-ddl-edit-as-sql` testids the E2E suite drives
 * exist in exactly ONE place — a second copy would trip Playwright's strict
 * mode the moment both surfaces mount.
 *
 * Inside the compact popover it is the LEFT half of `CompactDdlShell`'s single
 * pinned action row; the right half is where the arm's own [Cancel] [Save …]
 * buttons land (`FormActionsSlot`). Nothing here scrolls.
 *
 * `Design table` renders only when the caller carries a design seed
 * (create/alter-table — DROP and friends have nothing to design, so they get
 * no button rather than a disabled one) and is enabled only with a live
 * `connectionId`; the disabled state always explains itself through
 * `VqeTooltip` (never a native `title=`).
 *
 * `Close` (testid `vqe-ddl-edit-as-sql`, kept for the E2E suite) closes the
 * popover and focuses the statement in the SQL editor the user is already in —
 * it was labelled "Edit as SQL", the full tab's name for opening a new Query
 * tab, which it never did. It is never the primary: Design table is, whenever
 * the pair holds the row's primary action.
 *
 * The click handlers (and their `feature:vqe_ddl_card_action` analytics) stay
 * one level up in `CompactVqePopover` — this component only renders.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import { Settings } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { VqeTooltip } from '../../visual-query-editor/render/common/VqeTooltip';
import { vqeCopy } from '../../visual-query-editor/ux/vqeCopy';

export interface CompactDdlFooterActionsProps {
  /** Design-table seed mode, or `null` to render no Design-table button. */
  readonly designTable: { readonly mode: 'create' | 'alter' } | null;
  /** Gates the Design-table button — nullish disables it with a reason. */
  readonly connectionId?: string | null;
  readonly onDesignTable?: () => void;
  /** Close the card and focus the statement in the SQL editor. */
  readonly onEditAsSql: () => void;
  /**
   * Whether this pair holds the row's primary action. `true` (the read-only
   * card, the table arm) makes Design table the solid button. Arms whose form
   * owns a real primary (`Save changes`) in the SAME pinned row pass `false`
   * so the row has exactly one solid button.
   */
  readonly ownsPrimary?: boolean;
}

export function CompactDdlFooterActions({
  designTable,
  connectionId,
  onDesignTable,
  onEditAsSql,
  ownsPrimary = true,
}: CompactDdlFooterActionsProps) {
  const designTableTooltip = !connectionId
    ? vqeCopy.ddlCard.tooltips.designTableNoConnection
    : designTable?.mode === 'create'
      ? vqeCopy.ddlCard.tooltips.designTableCreate
      : vqeCopy.ddlCard.tooltips.designTableAlter;

  return (
    <div className="flex items-center gap-2">
      {designTable ? (
        <VqeTooltip content={designTableTooltip}>
          <Button
            type="button"
            variant={ownsPrimary ? 'default' : 'outline'}
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={onDesignTable}
            disabled={!connectionId}
            aria-label={vqeCopy.ddlCard.actions.designTable}
            data-testid="vqe-ddl-design-table"
          >
            <Settings className="h-3.5 w-3.5" />
            {vqeCopy.ddlCard.actions.designTable}
          </Button>
        </VqeTooltip>
      ) : null}
      <VqeTooltip content={vqeCopy.ddlCard.tooltips.close}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={onEditAsSql}
          aria-label={vqeCopy.ddlCard.actions.close}
          data-testid="vqe-ddl-edit-as-sql"
        >
          {vqeCopy.ddlCard.actions.close}
        </Button>
      </VqeTooltip>
    </div>
  );
}
